/**
 * Observation Module — Shared Core Page
 *
 * Supports both CYP ASD (10 tasks) and Adult ASD (13 tasks) variants via
 * configuration props. Each variant passes its own task list, seed data
 * helpers, storage key prefix, and breadcrumb label.
 *
 * Flow:
 *   1. Clinician starts on a full-page "task picker" — a grid of buttons,
 *      one per available task (e.g. "+ Description of a Picture").
 *   2. Clicking a task button adds it as the first observation task and
 *      switches to its form. The sidebar now appears on the left showing
 *      just that task's badge and a "+" button to add more.
 *   3. Clicking "+" in the sidebar returns to the picker with already-added
 *      tasks visually disabled so they can't be added twice.
 *   4. Task numbering is *order-based* (first picked = 1, second = 2, etc.),
 *      not determined by the task's config ID.
 *
 * The sidebar is compact by default (badge-only strip). A chevron at the top
 * expands it to reveal full task names, and that choice survives refresh.
 *
 * Architecture:
 *   • Left sidebar  — ObservationTaskSelector (only rendered when ≥1 task added)
 *   • Main panel    — Task picker | ObservationTaskPanel | ObservationSummary
 *   • State is fully local for the mockup phase.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, LockClosedIcon } from '@heroicons/react/solid';
import { useVideoStream } from '../../../context/VideoStreamContext';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import ObservationTaskSelector from './components/ObservationTaskSelector';
import ObservationTaskPanel from './components/ObservationTaskPanel';
import ObservationSummary from './components/ObservationSummary';
import GeneralObservationsPanel from './components/GeneralObservationsPanel';
import OBSERVATION_TASKS from './config/observationTasksConfig';
import { GENERAL_PANELS } from './config/generalObservationsConfig';
import {
  EMPTY_OBS_STATE,
  isDemoMode, setDemoMode,
} from '../seedData/cypAsdSeedData';

/**
 * CYP ASD Observation — thin wrapper passing the 10-task CYP config
 * to the shared ObservationModule core.
 */
export default function CypAsdObservation() {
  return (
    <ObservationModule
      tasks={OBSERVATION_TASKS}
      emptyState={EMPTY_OBS_STATE}
      isDemoModeFn={isDemoMode}
      setDemoModeFn={setDemoMode}
      storageKeyPrefix="cyp-asd-obs"
      breadcrumbLabel="CYP ASD Assessment"
    />
  );
}

/**
 * Core observation module — receives all variant-specific configuration
 * as props so it can power both CYP and Adult ASD variants without
 * duplicating the 700+ lines of UI logic.
 *
 * Props:
 *   tasks           — Array of task config objects to display
 *   emptyState      — Default empty state (selectedTaskIds, assessmentMode, etc.)
 *   isDemoModeFn    — (patientId) => boolean — checks if demo mode is on
 *   setDemoModeFn   — (patientId, on) => void — toggles demo mode
 *   storageKeyPrefix — prefix for localStorage key (e.g. "cyp-asd-obs")
 *   breadcrumbLabel  — middle breadcrumb segment (e.g. "CYP ASD Assessment")
 */
export function ObservationModule({
  tasks,
  emptyState,
  isDemoModeFn,
  setDemoModeFn,
  storageKeyPrefix,
  breadcrumbLabel,
}) {
  const { patientId } = useParams();
  const videoStream = useVideoStream();

  /**
   * Demo mode toggle — when ON the page loads with realistic dummy data
   * for demonstration purposes; when OFF the page starts empty so the
   * user can test the UI/UX by entering their own data.
   */
  const [demoMode, setDemoModeLocal] = useState(() => isDemoModeFn(patientId));

  const handleDemoToggle = useCallback(() => {
    const next = !demoMode;
    setDemoModeFn(patientId, next);
    setDemoModeLocal(next);
    window.location.reload();
  }, [demoMode, patientId, setDemoModeFn]);

  // Opt this page into the floating video panel while it's mounted
  useEffect(() => {
    videoStream?.setVideoEnabled(true);
    return () => videoStream?.setVideoEnabled(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Session-storage key for this patient's observation data. Keeps task
   * selections and form data alive across soft navigations/reloads without
   * requiring Firestore writes in this mockup phase. A hard refresh
   * (Cmd+Shift+R) clears localStorage so it acts as a reset.
   */
  const storageKey = `${storageKeyPrefix}-${patientId}`;

  /** All available task IDs in config order — used by "Pre-Set" mode. */
  const allTaskIdsInOrder = useMemo(
    () => tasks.map(t => t.id),
    [tasks]
  );

  /**
   * Assessment mode toggle:
   *   'build'  — clinician picks tasks one-by-one
   *   'preset' — all tasks pre-loaded in fixed order, sidebar visible (default)
   *
   * Persisted so switching survives page reloads.
   */
  const [assessmentMode, setAssessmentMode] = useState(() => {
    try {
      const cached = localStorage.getItem(storageKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        return parsed.assessmentMode || 'preset';
      }
    } catch { /* ignore */ }
    return emptyState.assessmentMode;
  });

  /**
   * Ordered list of task IDs in the sequence the clinician added them.
   * In "preset" mode without cached data, we auto-populate all tasks;
   * in "build" mode the clinician starts from an empty list.
   */
  const [selectedTaskIds, setSelectedTaskIds] = useState(() => {
    try {
      const cached = localStorage.getItem(storageKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        return parsed.selectedTaskIds || [];
      }
    } catch { /* ignore */ }
    return emptyState.selectedTaskIds;
  });

  /** The task config ID currently being viewed in the main panel, or null. */
  const [activeTaskId, setActiveTaskId] = useState(() => {
    try {
      const cached = localStorage.getItem(storageKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        return parsed.activeTaskId ?? null;
      }
    } catch { /* ignore */ }
    return emptyState.activeTaskId;
  });

  /**
   * Which view occupies the main panel:
   *   'picker'  — full-page task selection grid (initial state + "+" button)
   *   'task'    — a specific task's observation form
   *   'summary' — criteria-based summary across all tasks
   *   'general' — one of the General Observations sub-panels
   */
  const [activeView, setActiveView] = useState(() => {
    try {
      const cached = localStorage.getItem(storageKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        return parsed.activeView || 'picker';
      }
    } catch { /* ignore */ }
    return emptyState.activeView;
  });

  /**
   * Which General Observations sub-panel is active when activeView === 'general'.
   * Values: 'self_reported' | 'sensory' | null
   */
  const [activeGeneralPanel, setActiveGeneralPanel] = useState(() => {
    try {
      const cached = localStorage.getItem(storageKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        return parsed.activeGeneralPanel || null;
      }
    } catch { /* ignore */ }
    return emptyState.activeGeneralPanel || null;
  });

  /** All form data for all tasks, keyed by task config ID then field ID. */
  const [allTaskData, setAllTaskData] = useState(() => {
    try {
      const cached = localStorage.getItem(storageKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        return parsed.allTaskData || {};
      }
    } catch { /* ignore */ }
    return emptyState.allTaskData;
  });

  /**
   * Global toggle for the Section B "Observations" panel on each task.
   * When off, the per-task behavioural checkboxes are hidden entirely.
   * Persisted to localStorage alongside other observation state.
   */
  const [showObservations, setShowObservations] = useState(() => {
    try {
      const cached = localStorage.getItem(storageKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        return parsed.showObservations ?? false;
      }
    } catch { /* ignore */ }
    return false;
  });

  /**
   * Design toggle for the confidence input on the Review page:
   *   'ternary'    — 5-option scale (default)
   *   'percentage' — 0-100% segmented bar
   *
   * Lifted here so the toggle can live in the breadcrumb toolbar
   * while the actual input renders inside ObservationSummary.
   */
  const [confidenceStyle, setConfidenceStyle] = useState(() => {
    try {
      const cached = localStorage.getItem(storageKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        return parsed.confidenceStyle || 'ternary';
      }
    } catch { /* ignore */ }
    return 'ternary';
  });

  /** Persist observation state to localStorage whenever it changes. */
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        assessmentMode,
        selectedTaskIds,
        activeTaskId,
        activeView,
        activeGeneralPanel,
        allTaskData,
        showObservations,
        confidenceStyle,
      }));
    } catch { /* quota exceeded — ignore in mockup */ }
  }, [storageKey, assessmentMode, selectedTaskIds, activeTaskId, activeView, activeGeneralPanel, allTaskData, showObservations, confidenceStyle]);

  /**
   * Toggle between "Build Your Own" and "Pre-Set" assessment modes.
   *
   * Switching to "preset" auto-populates all tasks in config order
   * and lands the user on task 1. Switching back to "build" clears
   * the task list so the clinician starts fresh from the picker.
   * Form data (allTaskData) is intentionally preserved so nothing
   * the clinician already entered is lost.
   */
  const handleModeSwitch = useCallback((newMode) => {
    if (newMode === assessmentMode) return;
    setAssessmentMode(newMode);

    if (newMode === 'preset') {
      setSelectedTaskIds(allTaskIdsInOrder);
      setActiveTaskId(allTaskIdsInOrder[0]);
      setActiveView('task');
    } else {
      setSelectedTaskIds([]);
      setActiveTaskId(null);
      setActiveView('picker');
    }
  }, [assessmentMode, allTaskIdsInOrder]);

  // ── Task management ───────────────────────────────────────────────────

  /**
   * Add a task from the picker. It goes to the end of the ordered list
   * and immediately becomes the active task so its form is displayed.
   */
  const handleAddTask = useCallback((taskId) => {
    setSelectedTaskIds(prev => {
      if (prev.includes(taskId)) return prev;
      return [...prev, taskId];
    });
    setActiveTaskId(taskId);
    setActiveView('task');
  }, []);

  /**
   * Remove a task from the assessment. If it was the active task, fall
   * back to the first remaining task or the picker if none remain.
   */
  const handleRemoveTask = useCallback((taskId) => {
    setSelectedTaskIds(prev => {
      const next = prev.filter(id => id !== taskId);
      setActiveTaskId(current => {
        if (current === taskId) {
          if (next.length > 0) {
            setActiveView('task');
            return next[0];
          }
          setActiveView('picker');
          return null;
        }
        return current;
      });
      return next;
    });
  }, []);

  /** Navigate to a task that's already in the sidebar. */
  const handleSelectTask = useCallback((taskId) => {
    setActiveTaskId(taskId);
    setActiveView('task');
  }, []);

  /** Open the task picker (triggered by the "+" button in the sidebar). */
  const handleOpenPicker = useCallback(() => {
    setActiveView('picker');
  }, []);

  /** Navigate to the summary view. */
  const handleSelectSummary = useCallback(() => {
    setActiveView('summary');
  }, []);

  /** Navigate to a General Observations sub-panel. */
  const handleSelectGeneral = useCallback((panelId) => {
    setActiveGeneralPanel(panelId);
    setActiveView('general');
  }, []);

  /** Update a field on the currently active task. */
  const handleFieldChange = useCallback((fieldId, value) => {
    setAllTaskData(prev => ({
      ...prev,
      [activeTaskId]: {
        ...(prev[activeTaskId] || {}),
        [fieldId]: value,
      },
    }));
  }, [activeTaskId]);

  /**
   * Update a field on the active General Observations panel.
   * Keyed in allTaskData under 'general_<panelId>' to avoid collisions
   * with numeric task IDs.
   */
  const handleGeneralFieldChange = useCallback((fieldId, value) => {
    const key = `general_${activeGeneralPanel}`;
    setAllTaskData(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [fieldId]: value,
      },
    }));
  }, [activeGeneralPanel]);

  // ── Derived values ────────────────────────────────────────────────────

  const activeTask = tasks.find(t => t.id === activeTaskId);
  const activeTaskData = allTaskData[activeTaskId] || {};

  /**
   * Derive prev/next navigation targets from the ordered task list so the
   * sticky footer can show "Previous Task" / "Next Task" buttons.
   */
  const currentIndex = useMemo(
    () => (activeTaskId != null ? selectedTaskIds.indexOf(activeTaskId) : -1),
    [selectedTaskIds, activeTaskId]
  );
  const prevTaskId = currentIndex > 0 ? selectedTaskIds[currentIndex - 1] : null;
  const nextTaskId = currentIndex >= 0 && currentIndex < selectedTaskIds.length - 1
    ? selectedTaskIds[currentIndex + 1]
    : null;
  const prevTask = prevTaskId ? tasks.find(t => t.id === prevTaskId) : null;
  const nextTask = nextTaskId ? tasks.find(t => t.id === nextTaskId) : null;

  const activeGeneralTitle = activeGeneralPanel
    ? GENERAL_PANELS.find(p => p.id === activeGeneralPanel)?.title || null
    : null;

  const activeLabel = activeView === 'summary'
    ? 'Review'
    : activeView === 'general'
      ? `General Observations${activeGeneralTitle ? ` — ${activeGeneralTitle}` : ''}`
      : activeView === 'picker'
        ? null
        : activeTask
          ? `Task ${currentIndex + 1}. ${activeTask.shortTitle}`
          : null;

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}`],
    [breadcrumbLabel],
    ['Behavioural Observation'],
    ...(activeLabel ? [[activeLabel]] : []),
  ];

  return (
    <Layout activePage="dashboard">
      {/* Breadcrumb row — toggle lives on the right of the same line */}
      <div className="flex items-center">
        <Breadcrumb tupleList={breadcrumbTuples} />

        {/* Design toggles — mode + observations visibility */}
        <div className="flex-shrink-0 pr-8 flex items-center gap-5">
          {/* Demo data toggle */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 font-medium mr-1">Demo Data:</span>
            <button
              onClick={handleDemoToggle}
              className={`
                relative w-9 h-5 rounded-full transition-colors duration-200 flex-shrink-0
                ${demoMode ? 'bg-amber-500' : 'bg-gray-300'}
              `}
              title={demoMode ? 'Turn off demo data — start with a blank page' : 'Turn on demo data — pre-fill with example content'}
            >
              <span
                className={`
                  absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm
                  transition-transform duration-200
                  ${demoMode ? 'translate-x-4' : 'translate-x-0'}
                `}
              />
            </button>
          </div>

          <div className="h-5 border-l border-gray-200" />

          {/* Assessment mode toggle */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 font-medium mr-1">Mode:</span>
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => handleModeSwitch('build')}
                className={`
                  px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200
                  ${assessmentMode === 'build'
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'}
                `}
              >
                Build Your Own
              </button>
              <button
                onClick={() => handleModeSwitch('preset')}
                className={`
                  px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200
                  ${assessmentMode === 'preset'
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'}
                `}
              >
                Pre-Set (All Tasks)
              </button>
            </div>
          </div>

          {/* Observations section toggle */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 font-medium mr-1">Observations:</span>
            <button
              onClick={() => setShowObservations((v) => !v)}
              className={`
                relative w-9 h-5 rounded-full transition-colors duration-200 flex-shrink-0
                ${showObservations ? 'bg-primary' : 'bg-gray-300'}
              `}
              title={showObservations ? 'Hide observations section' : 'Show observations section'}
            >
              <span
                className={`
                  absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm
                  transition-transform duration-200
                  ${showObservations ? 'translate-x-4' : 'translate-x-0'}
                `}
              />
            </button>
          </div>

          {/* Confidence scale design toggle */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 font-medium mr-1">Confidence:</span>
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setConfidenceStyle('ternary')}
                className={`
                  px-2.5 py-1 rounded-md text-xs font-semibold transition-all duration-200
                  ${confidenceStyle === 'ternary'
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'}
                `}
              >
                4-Option
              </button>
              <button
                onClick={() => setConfidenceStyle('percentage')}
                className={`
                  px-2.5 py-1 rounded-md text-xs font-semibold transition-all duration-200
                  ${confidenceStyle === 'percentage'
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'}
                `}
              >
                Percentage
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-8rem)]">
        {/* Left sidebar — always mounted, animates width from 0 when no tasks
            to its natural width once the first task is added */}
        <ObservationTaskSelector
          tasks={tasks}
          selectedTaskIds={selectedTaskIds}
          activeTaskId={activeTaskId}
          activeView={activeView}
          assessmentMode={assessmentMode}
          allTaskData={allTaskData}
          activeGeneralPanel={activeGeneralPanel}
          onSelectTask={handleSelectTask}
          onRemoveTask={handleRemoveTask}
          onOpenPicker={handleOpenPicker}
          onSelectSummary={handleSelectSummary}
          onSelectGeneral={handleSelectGeneral}
        />

        {/* Main panel — scrollable content area + sticky footer nav */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Scrollable content */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {activeView === 'picker' ? (
              <TaskPicker
                tasks={tasks}
                selectedTaskIds={selectedTaskIds}
                onAddTask={handleAddTask}
              />
            ) : activeView === 'summary' ? (
              <ObservationSummary
                tasks={tasks}
                selectedTaskIds={selectedTaskIds}
                allTaskData={allTaskData}
                confidenceStyle={confidenceStyle}
              />
            ) : activeView === 'general' && activeGeneralPanel ? (
              <div className="p-6">
                <GeneralObservationsPanel
                  panelId={activeGeneralPanel}
                  data={allTaskData[`general_${activeGeneralPanel}`] || {}}
                  onFieldChange={handleGeneralFieldChange}
                />
              </div>
            ) : activeTask ? (
              <ObservationTaskPanel
                task={activeTask}
                taskData={activeTaskData}
                onFieldChange={handleFieldChange}
                selectedTaskIds={selectedTaskIds}
                showObservations={showObservations}
              />
            ) : null}
          </div>

          {/* Sticky footer nav — always visible when viewing a task, no scroll needed */}
          {activeView === 'task' && activeTask && (
            <div className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-3 flex items-center justify-between">
              {prevTask ? (
                <button
                  onClick={() => handleSelectTask(prevTaskId)}
                  className="flex items-center gap-2 py-2 px-4 rounded-lg text-sm font-semibold
                    text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors duration-150"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                  {prevTask.shortTitle}
                </button>
              ) : (
                <div />
              )}

              {/* Centre label — current task position */}
              <span className="text-xs text-gray-400 font-medium">
                Task {currentIndex + 1} of {selectedTaskIds.length}
              </span>

              {nextTask ? (
                <button
                  onClick={() => handleSelectTask(nextTaskId)}
                  className="flex items-center gap-2 py-2 px-4 rounded-lg text-sm font-semibold
                    text-white bg-primary hover:bg-primary/90 transition-colors duration-150"
                >
                  {nextTask.shortTitle}
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              ) : assessmentMode === 'preset' ? (
                <button
                  onClick={handleSelectSummary}
                  className="flex items-center gap-2 py-2 px-4 rounded-lg text-sm font-semibold
                    text-white bg-primary hover:bg-primary/90 transition-colors duration-150"
                >
                  Review
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleOpenPicker}
                  className="flex items-center gap-2 py-2 px-4 rounded-lg text-sm font-semibold
                    text-white bg-primary hover:bg-primary/90 transition-colors duration-150"
                >
                  Add another task
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>

      </div>
    </Layout>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// TaskPicker — Full-page grid for choosing which tasks to add
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Renders a grid of all available observation tasks as "+ Task Name" buttons.
 * Tasks that have already been added are shown as disabled/grayed out
 * so the clinician knows they can't be added again. Unavailable tasks
 * (content not yet built) show a lock icon.
 */
function TaskPicker({ tasks, selectedTaskIds, onAddTask }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-full px-8 py-12">
      {/* Page heading */}
      <div className="text-center mb-10">
        <div className="w-16 h-16 bg-primary/5 rounded-2xl flex items-center justify-center mx-auto mb-5">
          <svg className="w-8 h-8 text-primary/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </div>
        <h2 className="text-gray-900 text-xl font-bold mb-2">
          {selectedTaskIds.length === 0
            ? 'Select your first observation task'
            : 'Add another observation task'}
        </h2>
        <p className="text-gray-500 text-sm max-w-lg">
          Choose tasks to include in this assessment. You can add or remove
          tasks at any time as the session progresses.
        </p>
      </div>

      {/* Task grid */}
      <div className="grid grid-cols-2 gap-3 w-full max-w-2xl">
        {tasks.map((task) => {
          const isAlreadyAdded = selectedTaskIds.includes(task.id);
          const isAvailable = task.available;
          const isDisabled = isAlreadyAdded || !isAvailable;

          return (
            <button
              key={task.id}
              onClick={() => {
                if (!isDisabled) onAddTask(task.id);
              }}
              disabled={isDisabled}
              className={`
                relative flex items-center gap-3 px-4 py-3.5 rounded-xl text-left
                border transition-all duration-200 group
                ${isAlreadyAdded
                  ? 'bg-primary/5 border-primary/20 cursor-default'
                  : !isAvailable
                    ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-60'
                    : 'bg-white border-gray-200 hover:border-primary hover:shadow-md hover:shadow-primary/5 cursor-pointer'
                }
              `}
            >
              {/* Leading icon: checkmark for added, lock for unavailable, + for available */}
              <div className={`
                w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-sm
                transition-colors duration-200
                ${isAlreadyAdded
                  ? 'bg-primary/10 text-primary'
                  : !isAvailable
                    ? 'bg-gray-100 text-gray-400'
                    : 'bg-gray-100 text-gray-400 group-hover:bg-primary/10 group-hover:text-primary'
                }
              `}>
                {isAlreadyAdded ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : !isAvailable ? (
                  <LockClosedIcon className="w-3.5 h-3.5" />
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                )}
              </div>

              {/* Task name */}
              <div className="flex-1 min-w-0">
                <p className={`
                  text-sm font-medium leading-tight truncate
                  ${isAlreadyAdded ? 'text-primary' : !isAvailable ? 'text-gray-400' : 'text-gray-700'}
                `}>
                  {task.shortTitle}
                </p>
                {!isAvailable && (
                  <p className="text-[10px] text-gray-400 mt-0.5">Coming soon</p>
                )}
                {isAlreadyAdded && (
                  <p className="text-[10px] text-primary/60 mt-0.5">
                    Task {selectedTaskIds.indexOf(task.id) + 1} in your assessment
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
